import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import HandIcon from 'assets/images/easyPay/init.svg'
import Header from 'components/headers/addLoan'
import BankAccountCard from 'components/bankAccountCard'
import style from './finalAdd.module.scss'
import useSetJustAdd from './hooks/useSetJustAdd'
import useSetRealAdd from './hooks/useSetRealAdd'

/**
 * Where the loan actually happens, and where the two outcomes part company:
 * «اعطای وام» moves Hamyan's money to the account on the receipt, «وام تنها
 * ثبت شود» records the same loan and leaves the paying to the manager.
 *
 * The second one is why the borrower's card number is on this screen. Until
 * now the receipt named the account («نام - بانک») but never showed a digit
 * of it, so the manager who had just chosen to pay by hand had nowhere to
 * read the number the member had entered in their own request.
 */
export default function FinalAdd() {
    const { state } = useLocation()
    const { mutate: justMutate } = useSetJustAdd()
    const { mutate } = useSetRealAdd()
    const navigate = useNavigate()
    const account: IBankAccountCards | undefined = state?.payload?.bankAccount

    return (
        // flex column rather than absolutely-positioned buttons: the card
        // number below the receipt is real height, and on a short phone the
        // old `absolute bottom-10` bar sat on top of it
        <div className=" flex min-h-screen flex-col">
            <Header title="" />
            <div className=" relative text-sm">
                <img
                    src={HandIcon}
                    alt="hand"
                    className=" mx-auto absolute -top-16 z-50 right-32"
                />

                <div className={style['StyledReceipt']}>
                    <div>
                        <p className=" text-gray-1">واریز به حساب:</p>
                        <p>{state?.payload?.name}</p>
                    </div>
                    <hr className=" my-2" />
                    <div className=" flex justify-between my-2">
                        <p className=" text-gray-1">در حساب:</p>
                        <p>{state?.payload?.bankName}</p>
                    </div>
                    <div className=" flex justify-between my-2">
                        <p className=" text-gray-1">تعداد اقساط:</p>
                        <p>{state?.payload?.number_of_repayments}</p>
                    </div>
                    <div className=" flex justify-between my-2">
                        <p className=" text-gray-1">میزان کارمزد:</p>
                        <p>
                            {state?.payload?.interest_rate}{' '}
                            {state?.payload?.interest_type === 'percentage'
                                ? 'درصد'
                                : 'تومان'}
                        </p>
                    </div>
                    {state?.payload?.guarantor_names?.length > 0 && (
                        <div className=" flex justify-between my-2">
                            <p className=" text-gray-1">ضامن:</p>
                            <p>{state?.payload?.guarantor_names.join('، ')}</p>
                        </div>
                    )}
                    <div className=" flex justify-between my-2">
                        <p className=" text-gray-1">نام صندوق:</p>
                        <p>{state?.payload?.fundName}</p>
                    </div>
                    <div className=" flex justify-between my-2">
                        <p className=" text-gray-1"> بابت:</p>
                        <p>اعطای وام</p>
                    </div>

                    <hr className=" my-4" />
                    <div className=" flex justify-between my-2">
                        <p className=" text-gray-1"> مبلغ:</p>
                        <p>
                            {state?.payload?.amount &&
                                Number(
                                    state?.payload?.amount
                                ).toLocaleString()}{' '}
                            تومان
                        </p>
                    </div>
                </div>

                {account && (
                    <div className=" mx-[5%] mt-8">
                        <p className=" mb-2 text-xs text-gray-1">
                            اگر «وام تنها ثبت شود» را می‌زنید، پول را خودتان به
                            این شماره واریز کنید:
                        </p>
                        <BankAccountCard
                            account={account}
                            belongsToBorrower={
                                state?.payload?.receiver
                                    ? account.member === state.payload.receiver
                                    : undefined
                            }
                            borrowerName={state?.payload?.name}
                            source="loan_grant_final_add"
                        />
                    </div>
                )}
            </div>
            <div className=" mt-auto w-full pb-10 pt-8">
                <div className=" w-full">
                    <div
                        className=" mx-8 rounded-xl bg-yellow-1 text-white text-center py-2.5 my-2 cursor-pointer"
                        onClick={() => {
                            const body: IManagementBodyAddJust = {
                                amount: state?.payload?.amount,
                                interest_pay_mode:
                                    state?.payload?.interest_pay_mode,
                                interest_type: state?.payload?.interest_type,
                                interest_rate: state?.payload?.interest_rate,
                                fixed_interest_amount:
                                    state?.payload?.fixed_interest_amount,
                                installments: state?.response?.installments,
                                moneyPool: state?.payload?.moneyPool,
                                poolShip: state?.payload?.poolShip,
                                number_of_repayments:
                                    state?.payload?.number_of_repayments,
                                pay_date: state?.payload?.pay_date,
                                due_date: state?.payload?.due_date,
                                tag: state?.payload?.tag,
                                loan_demand: state?.loan_demand,
                                guarantor_ids:
                                    state?.payload?.guarantor_ids ?? [],
                            }
                            justMutate(body)
                        }}
                    >
                        وام تنها ثبت شود
                    </div>
                </div>
                <div className=" w-full">
                    <div
                        className=" mx-8 rounded-xl  bg-green-1 text-white text-center py-2.5 my-2 cursor-pointer"
                        onClick={() => {
                            // The card requirement lives HERE, on the only
                            // button that needs a destination account — not on
                            // the form two screens back, which also gates
                            // «وام تنها ثبت شود».
                            if (!account) {
                                toast.error(
                                    'برای واریز آنلاین، باید شماره حساب گیرنده ثبت شده باشد. می‌توانید وام را فقط ثبت کنید.'
                                )
                                return
                            }
                            const body: IManagementAddPayload = {
                                amount: state?.payload?.amount,
                                interest_pay_mode:
                                    state?.payload?.interest_pay_mode,
                                interest_type: state?.payload?.interest_type,
                                interest_rate: state?.payload?.interest_rate,
                                fixed_interest_amount:
                                    state?.payload?.fixed_interest_amount,
                                installments: state?.response?.installments,
                                moneyPool: state?.payload?.moneyPool,
                                poolShip: state?.payload?.poolShip,
                                number_of_repayments:
                                    state?.payload?.number_of_repayments,
                                pay_date: state?.payload?.pay_date,
                                due_date: state?.payload?.due_date,
                                tag: state?.payload?.tag,
                                bankAccount: state?.payload?.bank_account,
                                loan_demand: state?.loan_demand,
                                guarantor_ids:
                                    state?.payload?.guarantor_ids ?? [],
                                ctx_id: state?.payload.ctx_id,
                                ctx_type: 'mnp',
                                destination: 'mba',
                                is_group_pay: false,
                                payer_name: '',
                                receiver: state?.payload?.receiver,
                                receiver_name: '',
                                source: 'hbb',
                                state: '',
                                state_time: '',
                                transaction_code: '',
                            }
                            mutate(body)
                        }}
                    >
                        اعطای وام
                    </div>
                </div>
                <div className=" w-full">
                    <div
                        onClick={() => navigate(-1)}
                        className=" mx-8 rounded-xl  bg-transparent text-green-1 text-center py-2.5 my-2 cursor-pointer border-1 border-green-1"
                    >
                        بازگشت
                    </div>
                </div>
            </div>
        </div>
    )
}
